import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  IoLocationOutline,
  IoSearchOutline,
  IoStar,
} from "react-icons/io5";
import { http } from "../lib/http";
import { useI18n } from "../contexts/I18n";
import ELogo from "./ELogo";
import "./Explore.css";

const CATEGORIES = [
  {
    id: "barber",
    title: "Barber",
    slug: "barber",
    image: "/images/barbershop.jpg",
    titleAr: "حلاق",
    blurb: "Cuts & Grooming",
    blurbAr: "قصات & عناية",
  },
  {
    id: "hair-nail",
    title: "Hair & Nail",
    slug: "hair-&-nail",
    image: "/images/hair.jpg",
    titleAr: "صالون شعر وأظافر",
    blurb: "Style & Nails",
    blurbAr: "تصفيف & أظافر",
  },
  {
    id: "brows",
    title: "Brows & lashes",
    slug: "brows-&-lashes",
    image: "/images/brows.jpg",
    titleAr: "حواجب ورموش",
    blurb: "Brows & Lashes",
    blurbAr: "حواجب & رموش",
  },
  {
    id: "makeup",
    title: "Makeup",
    slug: "makeup",
    image: "/images/makeup.jpg",
    titleAr: "مكياج",
    blurb: "Day & Bridal",
    blurbAr: "يومي & عروس",
  },
  {
    id: "massage",
    title: "Massage",
    slug: "massage",
    image: "/images/massage.jpg",
    titleAr: "مساج",
    blurb: "Relax & Recover",
    blurbAr: "استرخاء & تعافي",
  },
  {
    id: "laser",
    title: "Laser Clinics",
    slug: "laser-clinics",
    image: "/images/hair1.jpg",
    titleAr: "عيادات الليزر",
    blurb: "Skin & Laser",
    blurbAr: "بشرة & ليزر",
  },
  {
    id: "tattoo",
    title: "Tatoo and Piercing",
    slug: "tatoo-and-piercing",
    image: "/images/tattoo1.jpg",
    titleAr: "وشم وثقب",
    displayEn: "Tattoo & Piercing",
    blurb: "Ink & Piercing",
    blurbAr: "وشم & ثقب",
  },
  {
    id: "pet",
    title: "Pet Services",
    slug: "pet-services",
    image: "/images/pet.png",
    titleAr: "خدمات الحيوانات الأليفة",
    blurb: "Pets & Grooming",
    blurbAr: "حيوانات & تجميل",
  },
];

const copy = {
  en: {
    headline: "Book beauty & wellness near you",
    sub: "Salons, spas, and clinics. Find a spot and reserve in minutes.",
    searchPh: "Search shops or services…",
    allTowns: "All towns",
    categoriesTitle: "Browse by service",
    categoriesSub: "Pick a category to see shops ready to book.",
    shopsTitle: "Ready to book",
    shopsSub: "Top-rated places matching your search.",
    shopsEmpty: "No shops match right now. Try another town or category.",
    shopsCount: (n) => `${n} shop${n === 1 ? "" : "s"}`,
    book: "Book",
    clearTown: "Clear town",
    home: "Home",
  },
  ar: {
    headline: "احجز خدمات الجمال والعافية قربك",
    sub: "صالونات وسبا وعيادات. اختر مكانك واحجز في دقائق.",
    searchPh: "ابحث عن متاجر أو خدمات…",
    allTowns: "كل المدن",
    categoriesTitle: "تصفح حسب الخدمة",
    categoriesSub: "اختر فئة لعرض المتاجر الجاهزة للحجز.",
    shopsTitle: "جاهز للحجز",
    shopsSub: "أفضل الأماكن المطابقة لبحثك.",
    shopsEmpty: "لا توجد متاجر مطابقة. جرّب مدينة أو فئة أخرى.",
    shopsCount: (n) => (n === 1 ? "متجر واحد" : `${n} متاجر`),
    book: "احجز",
    clearTown: "مسح المدينة",
    home: "الرئيسية",
  },
};

function buildServicePath(slug, town) {
  const base = `/service/${slug}`;
  return town ? `${base}/town/${encodeURIComponent(town)}` : base;
}

function bookingPath(shop) {
  return `/booking/${encodeURIComponent(shop.name || shop.slug)}`;
}

export default function Explore() {
  const { lang } = useI18n();
  const isAr = lang === "ar";
  const t = copy[isAr ? "ar" : "en"];

  const [searchQuery, setSearchQuery] = useState("");
  const [town, setTown] = useState("");
  const [activeCategory, setActiveCategory] = useState(null);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const { data } = await http.getJson("/businesses?limit=100&page=1");
        if (!cancelled) setShops(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Explore: failed to load shops", err);
        if (!cancelled) setShops([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const towns = useMemo(() => {
    const set = new Set();
    shops.forEach((s) => {
      if (s.town) set.add(s.town);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [shops]);

  const serviceCounts = useMemo(() => {
    const counts = {};
    shops.forEach((shop) => {
      if (!town || shop.town === town) {
        const cat = shop.serviceCategory;
        if (cat) counts[cat] = (counts[cat] || 0) + 1;
      }
    });
    return counts;
  }, [shops, town]);

  const q = searchQuery.trim().toLowerCase();

  const visibleCategories = useMemo(() => {
    if (!q) return CATEGORIES;
    return CATEGORIES.filter((c) => {
      const label = (c.displayEn || c.title).toLowerCase();
      return (
        label.includes(q) ||
        c.titleAr.includes(searchQuery.trim()) ||
        c.blurb.toLowerCase().includes(q) ||
        c.blurbAr.includes(searchQuery.trim())
      );
    });
  }, [q, searchQuery]);

  const bookableShops = useMemo(() => {
    let list = shops.slice();
    if (town) list = list.filter((s) => s.town === town);
    if (activeCategory) {
      list = list.filter((s) => s.serviceCategory === activeCategory);
    }
    if (q) {
      list = list.filter((s) => {
        const name = (s.name || s.slug || "").toLowerCase();
        const cat = (s.serviceCategory || "").toLowerCase();
        const townName = (s.town || "").toLowerCase();
        return name.includes(q) || cat.includes(q) || townName.includes(q);
      });
    }
    return list
      .sort((a, b) => (b.rating || 0) - (a.rating || 0))
      .slice(0, 12);
  }, [shops, town, activeCategory, q]);

  return (
    <div className={`ex ${isAr ? "rtl" : ""}`} dir={isAr ? "rtl" : "ltr"}>
      {/* Hero — brand + one headline + search only (no photo) */}
      <header className="ex-hero" aria-labelledby="ex-headline">
        <div className="ex-hero__glow" aria-hidden="true" />

        <div className="ex-hero__bar">
          <Link to="/" className="ex-logo-link" aria-label={t.home}>
            <ELogo size="medium" />
          </Link>
        </div>

        <div className="ex-hero__copy">
          <h1 id="ex-headline" className="ex-headline">
            {t.headline}
          </h1>
          <p className="ex-sub">{t.sub}</p>
        </div>
      </header>

      {/* Search + town filter side by side */}
      <div className="ex-toolbar">
        <form
          className="ex-search"
          role="search"
          onSubmit={(e) => e.preventDefault()}
        >
          <IoSearchOutline className="ex-search__icon" aria-hidden="true" />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.searchPh}
            aria-label={t.searchPh}
          />
        </form>

        <label className="ex-town">
          <IoLocationOutline aria-hidden="true" />
          <select
            value={town}
            onChange={(e) => setTown(e.target.value)}
            aria-label={t.allTowns}
          >
            <option value="">{t.allTowns}</option>
            {towns.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        {town ? (
          <button
            type="button"
            className="ex-town-clear"
            onClick={() => setTown("")}
          >
            {t.clearTown}
          </button>
        ) : null}
      </div>

      <main className="ex-main">
        {/* Categories */}
        <section className="ex-section ex-section--cats" aria-labelledby="ex-cats-title">
          <header className="ex-section__head">
            <h2 id="ex-cats-title">{t.categoriesTitle}</h2>
            <p>{t.categoriesSub}</p>
          </header>

          {loading ? (
            <div className="ex-cat-grid" aria-hidden="true">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="ex-skel" />
              ))}
            </div>
          ) : (
            <div className="ex-cat-grid" role="list">
              {visibleCategories.map((cat) => {
                const count = serviceCounts[cat.title] || 0;
                const active = activeCategory === cat.title;
                const label = isAr ? cat.titleAr : cat.displayEn || cat.title;
                const blurb = isAr ? cat.blurbAr : cat.blurb;
                return (
                  <article
                    key={cat.id}
                    className={`ex-cat ${active ? "is-active" : ""}`}
                    role="listitem"
                  >
                    <button
                      type="button"
                      className="ex-cat__photo"
                      onClick={() => {
                        setActiveCategory(active ? null : cat.title);
                        document
                          .getElementById("ex-shops-title")
                          ?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                      aria-pressed={active}
                      aria-label={label}
                    >
                      <img src={cat.image} alt="" loading="lazy" />
                      {count > 0 ? (
                        <span className="ex-cat__count">{count}</span>
                      ) : null}
                    </button>
                    <div className="ex-cat__body">
                      <h3>{label}</h3>
                      <p>{blurb}</p>
                      <Link
                        to={buildServicePath(cat.slug, town || undefined)}
                        className="ex-cat__all"
                      >
                        {isAr ? "عرض الكل" : "View all"}
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        {/* Bookable shops */}
        <section className="ex-section ex-section--shops" aria-labelledby="ex-shops-title">
          <header className="ex-section__head">
            <h2 id="ex-shops-title">{t.shopsTitle}</h2>
            <p>{t.shopsSub}</p>
          </header>

          {loading ? (
            <div className="ex-shop-grid" aria-hidden="true">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="ex-skel ex-skel--shop" />
              ))}
            </div>
          ) : bookableShops.length === 0 ? (
            <p className="ex-empty">{t.shopsEmpty}</p>
          ) : (
            <div className="ex-shop-grid">
              {bookableShops.map((shop) => (
                <article key={shop._id || shop.slug} className="ex-shop">
                  <Link to={bookingPath(shop)} className="ex-shop__link">
                    <div className="ex-shop__photo">
                      <img
                        src={shop.imageUrl || "/images/shop-placeholder.png"}
                        alt=""
                        loading="lazy"
                        onError={(e) => {
                          e.currentTarget.src = "/images/shop-placeholder.png";
                        }}
                      />
                    </div>
                    <div className="ex-shop__body">
                      <div className="ex-shop__top">
                        <h3>{shop.name || shop.slug}</h3>
                        {shop.rating != null ? (
                          <span className="ex-shop__rating">
                            <IoStar aria-hidden="true" />
                            {Number(shop.rating).toFixed(1)}
                          </span>
                        ) : null}
                      </div>
                      <p className="ex-shop__meta">
                        {shop.serviceCategory}
                        {shop.town ? ` · ${shop.town}` : ""}
                      </p>
                      <span className="ex-shop__cta">{t.book}</span>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
